import {
  PropertyHasChangedData,
  ScenePrimType,
  IVec3,
  VariableColorModeValues,
  Vec3d,
  SceneLayer,
  EngineMode
} from '@services/engine/types'
import {
  initialState as initialSceneState,
  SceneState,
  setPropertiesState,
  setSelectedFontPostScriptName,
  setPropertyPathValue
} from '@store/slices/sceneSlice'
import { put, select } from 'redux-saga/effects'
import Context from '@store/middleware/context'
import { radToDeg } from '@services/engine/utils'
import { RootState } from '@store/store'
import { type TQFont } from '@coretech/typequestweb'

export function* handlePropertyHasChanged(
  data: PropertyHasChangedData
): Generator<any, void, any> {
  try {
    if (!Context.Engine) return

    const { path, panelId, value } = data

    yield put(
      setPropertyPathValue({
        propertyPath: path,
        value
      })
    )

    const primitiveType: ScenePrimType = yield select(
      (state: RootState) => state.scene.primitiveType
    )

    /*
    ----------------- Primitive paths -----------------
    ---------------------------------------------------
    ---------------------------------------------------
    */
    if (path === 'shapeMaterial.pbrColor' || path === 'shapeMaterial.color') {
      const activeLayer: SceneLayer = yield select(
        (state: RootState) => state.scene.activeLayer
      )
      const styleMode = yield select((state: RootState) => state.scene.mode)

      if (activeLayer === '3D') {
        return yield setState('materialColor', engineRGBToHex(value))
      } else {
        if (styleMode === EngineMode.NORMAL) {
          return yield setState('materialColor', engineRGBToHex(value))
        } else if (styleMode === EngineMode.OUTLINE) {
          return yield setState('materialEColorLig', engineRGBToHex(value))
        }
      }
    }

    if (path === 'shapeMaterial.pbrRoughness') {
      return yield setState('materialRoughness', value)
    }

    if (path === 'shapeMaterial.pbrMetalness') {
      return yield setState('materialMetalness', value)
    }

    if (path === 'shapeMaterial.opacity') {
      return yield setState('materialOpacity', value)
    }

    if (path === 'shapeMaterial.pbrIsReflective') {
      return yield setState('materialReflective', value)
    }

    if (path === 'treeNode.location.position') {
      const activeLayer: SceneLayer = yield select(
        (state: RootState) => state.scene.activeLayer
      )

      if (activeLayer === '3D') {
        return yield setMultipleStates({
          transformPositionX: value.x,
          transformPositionY: value.y,
          transformPositionZ: value.z
        })
      } else {
        return yield setMultipleStates({
          transformPositionX: value.x,
          transformPositionY: value.y
        })
      }
    }

    if (path === 'treeNode.location.rotation') {
      const activeLayer: SceneLayer = yield select(
        (state: RootState) => state.scene.activeLayer
      )

      if (activeLayer === '3D') {
        return yield setMultipleStates({
          transformRotationX: radToDeg(value.x),
          transformRotationY: radToDeg(value.y),
          transformRotationZ: radToDeg(value.z)
        })
      } else {
        return yield setState('transformRotationX', radToDeg(value))
      }
    }

    if (path === 'treeNode.standard.name') {
      return yield setState('elementName', value)
    }

    if (path === 'treeNode.standard.blendMode') {
      return yield setState('blendType', value)
    }

    if (path === 'treeNode.standard.blendShape') {
      return yield setState('blendAmount', value)
    }

    if (path === 'treeNode.drawMaterial') {
      return yield setState('drawMaterial', value)
    }

    if (path === 'treeNode.standard.repeatMode') {
      return yield setState('repeatType', value)
    }

    if (path === 'treeNode.standard.repeatX') {
      return yield setState('repeatX', value)
    }

    if (path === 'treeNode.standard.repeatY') {
      return yield setState('repeatY', value)
    }

    if (path === 'treeNode.standard.repeatZ') {
      return yield setState('repeatZ', value)
    }

    if (path === 'treeNode.standard.distanceX') {
      return yield setState('repeatDistanceX', value)
    }

    if (path === 'treeNode.standard.distanceY') {
      return yield setState('repeatDistanceY', value)
    }

    if (path === 'treeNode.standard.distanceZ') {
      return yield setState('repeatDistanceZ', value)
    }

    if (path === 'treeNode.standard.circularCopyAxis') {
      return yield setState('repeatAngleDirection', value)
    }

    if (path === 'treeNode.standard.circularCopyCount') {
      return yield setState('repeatAngle', value)
    }

    if (path === 'treeNode.standard.circularCopyDistance') {
      return yield setState('repeatDistance', value)
    }

    if (path === 'treeNode.standard.alongX') {
      return yield setState('symmetryX', value)
    }

    if (path === 'treeNode.standard.alongY') {
      return yield setState('symmetryY', value)
    }

    if (path === 'treeNode.standard.alongZ') {
      return yield setState('symmetryZ', value)
    }

    if (path === 'treeNode.standard.onion') {
      return yield setState('primitiveShell', value)
    }

    if (path === 'treeNode.standard.round') {
      return yield setState('primitiveInflate', value)
    }

    if (path === 'treeNode.profile.textBoxSize') {
      const { x, y } = value
      return yield setMultipleStates({
        transformScaleX: x,
        transformScaleZ: y
      })
    }

    if (path === 'treeNode.standard.thickness') {
      return yield setState('primitiveHole', value)
    }

    if (path === 'treeNode.dimension.mode') {
      return yield setState('primitiveDimensionType', value)
    }

    // We multiply by 2 because the extrudeAmount denotes the extrusion amount in one direction, even though it's being applied in both directions, and transformScaleY represents the total height of the primitive.
    if (path === 'treeNode.dimension.extrudeAmount') {
      if (
        primitiveType === ScenePrimType.SOFT_BOX ||
        primitiveType === ScenePrimType.STAR ||
        primitiveType === ScenePrimType.TRIANGLE
      ) {
        return yield setState('transformScaleY', value * 2)
      }

      if (primitiveType === ScenePrimType.HORSESHOE) {
        return yield setState('transformScaleY', value)
      }

      if (primitiveType === ScenePrimType.TEXT) {
        return yield setState('transformScaleY', value * 2)
      }
    }

    if (path === 'treeNode.dimension.bottomFillet') {
      return yield setState('primitiveCornerOne', value)
    }

    if (path === 'treeNode.dimension.topFillet') {
      return yield setState('primitiveCornerTwo', value)
    }

    if (path === 'treeNode.dimension.distance') {
      return yield setState('primitiveDistance', value)
    }

    if (path === 'treeNode.dimension.rotation') {
      return yield setState('primitiveRotation', value)
    }

    if (path === 'treeNode.profile.width') {
      return yield setState('transformScaleX', value)
    }

    if (path === 'treeNode.profile.height') {
      const activeLayer: SceneLayer = yield select(
        (state: RootState) => state.scene.activeLayer
      )

      if (activeLayer === '3D') {
        if (primitiveType === ScenePrimType.TRIANGLE) {
          return yield setState('transformScaleZ', value / 2)
        } else {
          return yield setState('transformScaleZ', value)
        }
      } else {
        return yield setState('transformScaleY', value)
      }
    }

    if (path === 'treeNode.profile.cornerRounding0') {
      yield setState('primitiveRadial0', value)
    }

    if (path === 'treeNode.profile.cornerRounding1') {
      yield setState('primitiveRadial1', value)
    }

    if (path === 'treeNode.profile.cornerRounding2') {
      yield setState('primitiveRadial2', value)
    }

    if (path === 'treeNode.profile.cornerRounding3') {
      yield setState('primitiveRadial3', value)
    }

    if (path === 'treeNode.profile.angle') {
      if (primitiveType === ScenePrimType.STAR) {
        return yield setState('primitiveStarAngle', value)
      }

      if (primitiveType === ScenePrimType.HORSESHOE) {
        return yield setState('primitiveHorseshoeAngle', value)
      }

      if (primitiveType === ScenePrimType.TRIANGLE) {
        return yield setState('primitiveHorseshoeAngle', value)
      }
    }

    if (path === 'treeNode.profile.radius') {
      if (primitiveType === ScenePrimType.STAR) {
        return yield setState('transformScaleX', value)
      }

      if (primitiveType === ScenePrimType.HORSESHOE) {
        return yield setMultipleStates({
          primitiveHorseshoeRadius: value,
          transformScaleZ: value
        })
      }
    }

    if (path === 'treeNode.profile.width') {
      if (primitiveType === ScenePrimType.STAR) {
        return yield setState('transformScaleX', value)
      }

      if (primitiveType === ScenePrimType.HORSESHOE) {
        return yield setState('primitiveHorseshoeWidth', value)
      }
    }

    if (path === 'treeNode.profile.length') {
      if (primitiveType === ScenePrimType.HORSESHOE) {
        return yield setState('primitiveHorseshoeLength', value)
      }
    }

    if (path === 'treeNode.profile.thickness') {
      yield setState('primitiveHorseshoeThickness', value)
    }

    if (path === 'treeNode.profile.corners') {
      yield setState('primitiveHorseshoeCorner', value)
    }

    if (path === 'treeNode.profile.dartOffset') {
      yield setState('triangleVertex', value)
    }

    if (path === 'treeNode.profile.sideCount') {
      yield setState('primitiveStarPoints', value)
    }

    if (path === 'treeNode.profile.roundingRadius') {
      yield setState('primitiveStarCorners', value)
    }

    if (path === 'treeNode.profile.radiusA') {
      yield setMultipleStates({
        transformScaleX: value
      })
    }

    if (path === 'treeNode.profile.radiusB') {
      // value corresponding to treeNode.radiusB is twice the value corresponding to treeNode.radiusA
      return yield setMultipleStates({
        transformScaleZ: value * 2
      })
    }

    if (path === 'shapeMaterial.exprMaterialMode') {
      const mode = VariableColorModeValues[value]
      return yield setState('variableColorMode', mode ?? 0)
    }

    if (path === 'shapeMaterial.exprColorTop') {
      return yield setState('materialEColorTop', engineRGBToHex(value))
    }

    if (path === 'shapeMaterial.exprColorLight') {
      return yield setState('materialEColorLig', engineRGBToHex(value))
    }

    if (path === 'shapeMaterial.exprColorDark') {
      return yield setState('materialEColorSha', engineRGBToHex(value))
    }

    if (path === 'shapeMaterial.exprSpecularSize') {
      return yield setState('materialESpecularSize', value)
    }

    if (path === 'shapeMaterial.exprSpecularIntensity') {
      return yield setState('materialESpecularIntensity', value)
    }

    if (path === 'shapeMaterial.illustrativeMaterialMode') {
      const mode = VariableColorModeValues[value]
      return yield setState('variableColorMode', mode ?? 0)
    }

    if (path === 'shapeMaterial.illustrativeColor') {
      return yield setState('materialIColor', engineRGBToHex(value))
    }

    if (path === 'shapeMaterial.illustrativeEmissiveColor') {
      return yield setState('materialIEmissiveColor', engineRGBToHex(value))
    }

    if (path === 'shapeMaterial.illustrativeStrokeSize') {
      return yield setState('materialIStrokeSize', value)
    }

    if (path === 'shapeMaterial.illustrativeSpecularIntensity') {
      return yield setState('materialIHighlightIntensity', value)
    }

    if (path === 'shapeMaterial.illustrativeNormalIntensity') {
      return yield setState('materialIStrokeIntensity', value)
    }

    if (path === 'shapeMaterial.illustrativeColorVariation') {
      return yield setState('materialIColorVarIntensity', value)
    }

    if (path === 'shapeMaterial.illustrativeScaleX') {
      return yield setState('materialIScaleX', value)
    }

    if (path === 'shapeMaterial.illustrativeScaleY') {
      return yield setState('materialIScaleY', value)
    }

    if (path === 'shapeMaterial.illustrativeAngle') {
      return yield setState('materialIAngle', value)
    }

    /*
    ----------------- Global paths -----------------
    ---------------------------------------------------
    ---------------------------------------------------
    */

    if (path === 'cameraLens.distortionAmount') {
      return yield setState('cameraDistortion', value)
    }

    if (path === 'cameraLens.dofAperture') {
      return yield setState('cameraAperture', value)
    }

    if (path === 'cameraLens.dofFocalPlane') {
      return yield setState('cameraFocalPlane', value)
    }

    if (path === 'cameraLens.perspFocalLength') {
      return yield setState('cameraFocalLength', value)
    }

    if (path === 'cameraLens.orthoSize') {
      return yield setState('cameraSize', value)
    }

    if (path === 'background.colorA') {
      return yield setState('backgroundColorA', engineRGBToHex(value))
    }

    if (path === 'background.colorB') {
      return yield setState('backgroundColorB', engineRGBToHex(value))
    }

    if (path === 'background.gradientMode') {
      return yield setState('backgroundType', value)
    }

    if (path === 'frame.frameType') {
      return yield setState('frameType', value)
    }

    if (path === 'frame.size') {
      return yield setState('frameSize', { w: value.x, h: value.y })
    }

    if (path === 'frame.opacity') {
      return yield setState('frameOpacity', value)
    }

    if (path === 'floor.enabled') {
      return yield setState('floorEnabled', value)
    }

    if (path === 'floor.height') {
      return yield setState('floorHeight', value)
    }

    if (path === 'frame.enabled') {
      return yield setState('frameEnabled', value)
    }

    if (path === 'cameraLens.type') {
      return yield setState('cameraType', value)
    }

    if (path === 'style.style') {
      // Add 1 from value because the engine returns a value that is 1 less than what it is on the JS side (this enum in the engine starts at 0, whereas on the JS side it starts at 1)
      return yield setState('mode', value + 1)
    }

    if (path === 'style.exprOutline') {
      return yield setState('modeExpressiveOutline', value)
    }

    if (path === 'style.exprColor') {
      return yield setState('modeOutlineColor', engineRGBToHex(value))
    }

    if (path === 'style.pixelArtPixelCount') {
      return yield setState('modePixelSize', value)
    }

    if (path === 'style.illusLightTexture') {
      return yield setState('modeIllustrativeLightTexture', value)
    }

    if (path === 'style.illusShadowTexture') {
      return yield setState('modeIllustrativeShadowTexture', value)
    }

    if (path === 'style.illusGlobalStrokeSize') {
      return yield setState('modeIllustrativeGlobalStrokeSize', value)
    }

    if (path === 'treeNode.profile.text') {
      yield setState('textCopy', value)
    }

    if (path === 'treeNode.profile.size') {
      const primitiveType: ScenePrimType = yield select(
        (state: RootState) => state.scene.primitiveType
      )

      if (primitiveType === ScenePrimType.TEXT) {
        return yield setState('textSize', value)
      }
    }

    if (path === 'treeNode.profile.font') {
      yield setState('textFont', value)

      if (value) {
        try {
          // This promise is not resolved until the user actually searches for a font and one is returned
          const font: TQFont | undefined =
            yield Context.TypeQuestWeb.getFont(value)

          if (font) {
            yield put(setSelectedFontPostScriptName(font.postscriptName))
          }
        } catch (error) {
          console.error(error)
        }
      } else {
        /* The default value for treeNode.font is an empty string. If the engine reports that treeNode.font is an empty string, we need to set the selectedFontPostScriptName accordingly.
         */
        yield put(
          setSelectedFontPostScriptName(
            initialSceneState['selectedFontPostScriptName']
          )
        )
      }

      return
    }

    if (path === 'treeNode.profile.lineHeight') {
      return yield setState('textLineHeight', value)
    }

    if (path === 'treeNode.profile.letterSpacing') {
      return yield setState('textLetterSpacing', value)
    }

    if (path === 'treeNode.profile.leadingType') {
      return yield setState('textLeadingType', value)
    }

    if (path === 'lightOcclusion.distance') {
      return yield setState('lightOcclusionDistance', value)
    }

    if (path === 'directionalLight.direction') {
      const direction = value as Vec3d
      const currLightAngle1: number = yield select(
        (state: RootState) => state.scene.lightAngle1
      )

      // The calculation below will map the direction vector returned by the engine
      // to an angle of 0 regardless of the user inputting 0 or 360 for the angle.
      // So this checks what the user had entered before calling setState, so we know
      // if we need to pass 0 or 360 into setState (or neither).
      if (currLightAngle1 === 0) {
        yield setState('lightAngle1', currLightAngle1)
      } else {
        let lightAngle1 = radToDeg(Math.atan2(direction.z, direction.x)) % 360
        if (lightAngle1 <= 0) lightAngle1 = lightAngle1 + 360
        yield setState('lightAngle1', lightAngle1)
      }

      let lightAngle2 = radToDeg(Math.acos(direction.y)) % 360
      if (lightAngle2 < 0) lightAngle2 = lightAngle2 + 360
      yield setState('lightAngle2', lightAngle2)
      return
    }
  } catch (err) {
    console.error(err)
  }
}

function* setState<T extends keyof SceneState>(key: T, value: SceneState[T]) {
  yield put(
    setPropertiesState({
      [key]: value
    })
  )
}

function* setMultipleStates(kvPair: Partial<SceneState>) {
  yield put(
    setPropertiesState({
      ...kvPair
    })
  )
}

function engineRGBToHex(rgb: IVec3): string {
  return Context.Engine!.rGBToHex(rgb.x, rgb.y, rgb.z)
}
